import { ArrowDownCircleIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import CommonModal from "../common11/commonModals/CommonModal";
import CommonDatePicker from "../common11/formInputs/CommonDatePicker";
import InputField from "../common11/formInputs/InputField";
import SearchDropdown from "../common11/formInputs/SearchDropdown";
import EnhancedDynamicTablePagination from "../common/CommonTable/EnhancedDynamicTablePagination";
import { showErrorToast } from "../common11/toastUtils";
import {
  fetchOtLookups,
  fetchOtReservations,
  fetchOtTables,
} from "../../store/slices/otNoteSlice";
import OtModalFilterRow from "./OtModalFilterRow";

const getDefaultFilters = () => {
  const today = new Date().toISOString().slice(0, 10);
  return {
    patientSearch: "",
    fromDate: today,
    toDate: today,
    theatreName: null,
    tableName: null,
  };
};

const filterFieldClass = "flex-[1] min-w-[9rem]";

/** Dashboard placeholder — live data comes from Reservation Listing modal / API. */
export const getMockOtReservationList = () => [];

function OTReservationListingModal({ isOpen, onClose, onSelectPatient }) {
  const dispatch = useDispatch();
  const {
    reservations,
    reservationsLoading,
    theatres,
    tables,
  } = useSelector((state) => state.otNote);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: getDefaultFilters(),
  });

  const watchedTheatre = watch("theatreName");

  const loadReservations = async (filters) => {
    const theatreId = filters.theatreName?.id ?? filters.theatreName?.value ?? null;
    const tableId = filters.tableName?.id ?? filters.tableName?.value ?? null;
    const result = await dispatch(
      fetchOtReservations({
        patientSearch: filters.patientSearch || undefined,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        otId: theatreId || undefined,
        otTableId: tableId || undefined,
      })
    );
    if (fetchOtReservations.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to load reservations");
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    dispatch(fetchOtLookups());
    const defaults = getDefaultFilters();
    reset(defaults);
    setPage(0);
    loadReservations(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (!isOpen) return;
    const otId = watchedTheatre?.id ?? watchedTheatre?.value ?? null;
    dispatch(fetchOtTables(otId || undefined));
    if (!otId) {
      setValue("tableName", null);
    }
  }, [watchedTheatre, isOpen, dispatch, setValue]);

  const tableRows = useMemo(() => reservations || [], [reservations]);

  const handleSearch = (values) => {
    setPage(0);
    loadReservations(values);
  };

  const handleResetFilters = () => {
    const defaults = getDefaultFilters();
    reset(defaults);
    setPage(0);
    loadReservations(defaults);
  };

  const handleRowSelect = (row) => {
    onSelectPatient?.(row);
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={onClose}
      title="Reservation Listing"
      size="xl"
      disableBackdropClose={false}
      showCloseButton={true}
      actions={[]}
    >
      <div className="space-y-3">
        <OtModalFilterRow onReset={handleResetFilters} onSubmit={handleSubmit(handleSearch)}>
          <div className="flex-[2] min-w-[12rem]">
            <InputField
              name="patientSearch"
              control={control}
              label="Search patient"
              placeholder="Name / UHID / OPD no."
              inputSize="h-8"
            />
          </div>
          <div className={filterFieldClass}>
            <CommonDatePicker name="fromDate" control={control} label="From Date" maxDate={watch("toDate")} />
          </div>
          <div className={filterFieldClass}>
            <CommonDatePicker name="toDate" control={control} label="To Date" minDate={watch("fromDate")} />
          </div>
          <div className={filterFieldClass}>
            <SearchDropdown
              name="theatreName"
              control={control}
              dataArray={theatres}
              label="Theatre Name"
              isSearchable
              isClearable
              selectSize="h-8"
            />
          </div>
          <div className={filterFieldClass}>
            <SearchDropdown
              name="tableName"
              control={control}
              dataArray={tables}
              label="Table Name"
              isSearchable
              isClearable
              selectSize="h-8"
            />
          </div>
        </OtModalFilterRow>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col">
          {reservationsLoading ? (
            <div className="px-3 py-8 text-center text-sm text-gray-500">Loading reservations…</div>
          ) : null}
          <EnhancedDynamicTablePagination
            dataResult={tableRows}
            setDataResult={() => {}}
            fetchData={() => {}}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            count={tableRows.length}
            removeHeaders={[
              "reservationId",
              "otReservationId",
              "regId",
              "opdIpdId",
              "opdIpd",
              "doctorId",
              "theatreId",
              "tableId",
              "otId",
              "otTableId",
              "anaesthetistId",
              "surgeryId",
              "surgeryStartTime",
              "surgeryEndTime",
              "duration",
            ]}
            showPaginationBar={true}
            stickyLeftColumns={["patientName", "uhid"]}
            highlightRow={true}
            customRowBgColor="#E9F5FE"
            customRowBorderColor="#217E90"
            onRowDoubleClick={handleRowSelect}
            renderActions={(row) => (
              <div className="flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => handleRowSelect(row)}
                  className="inline-flex items-center justify-center text-[#217E90] hover:text-[#1a5e70]"
                  title="Select patient for OT Note"
                >
                  <ArrowDownCircleIcon className="h-4 w-4" />
                </button>
              </div>
            )}
            actionsPosition="start"
            stickyActions={true}
            tableClass="min-h-[320px]"
          />
        </div>
      </div>
    </CommonModal>
  );
}

export default OTReservationListingModal;
